"use client"

import { useState, useEffect, useRef } from "react"
import { Link, useNavigate, useLocation } from "react-router-dom"
import { loginUser } from "../utils/auth"

const Login = ({ onLogin }) => {
  const [formData, setFormData] = useState({
    role: "client",
    email: "",
    password: "",
    rememberMe: false,
  })
  const [message, setMessage] = useState("")
  const [isSuccess, setIsSuccess] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  const initialUrl = useRef(null)

  useEffect(() => {
    initialUrl.current = window.location.search
  }, [])

  const handleChange = (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value
    setFormData({ ...formData, [e.target.name]: value })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsLoading(true)
    try {
      const response = await loginUser(formData)

      // Call the onLogin function with user data
      onLogin(response.user)

      setMessage(response.message)
      setIsSuccess(true)
      console.log("Login successful", response)

      // Check if there's a pending URL from the extension
      const pendingUrl = localStorage.getItem("pendingUrl")

      if (pendingUrl) {
        console.log("Redirecting to home with pending URL:", pendingUrl)
        navigate("/")
        return
      }

      // Handle normal login redirects
      const userRole = response.user.role
      console.log("User object:", response.user)
      if (userRole === "admin") {
        navigate("/admin")
      } else if (userRole === "client") {
        navigate("/")
      } else {
        navigate("/")
      }
    } catch (error) {
      console.error("Login error", error)
      setMessage(error.response?.data.message || "Login failed. Please try again.")
      setIsSuccess(false)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="login-page">
      <style jsx="true">{`
                .login-page {
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    min-height: 100vh;
                    background-color: #f9fafb;
                    padding: 1rem;
                }
                
                .login-container {
                    background-color: #fff;
                    padding: 2.5rem;
                    border-radius: 0.5rem;
                    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
                    width: 100%;
                    max-width: 28rem;
                }
                
                .logo-container {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin-bottom: 1.5rem;
                }
                
                .logo-icon {
                    height: 2.5rem;
                    width: 2.5rem;
                    background-color: #007BFF;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: white;
                    font-weight: bold;
                    font-size: 1.25rem;
                    border-radius: 0.25rem;
                }
                
                .logo-text {
                    margin-left: 0.5rem;
                    font-size: 1.25rem;
                    font-weight: 600;
                }
                
                .logo-text-bold {
                    font-weight: 700;
                }
                
                .form-title {
                    font-size: 1.5rem;
                    font-weight: bold;
                    margin-bottom: 1.5rem;
                    text-align: center;
                    color: #333;
                }
                
                .message {
                    margin-bottom: 1rem;
                    padding: 0.75rem;
                    text-align: center;
                    border-radius: 0.375rem;
                    font-weight: 500;
                }
                
                .message-success {
                    background-color: #d1fae5;
                    color: #047857;
                }
                
                .message-error {
                    background-color: #fee2e2;
                    color: #b91c1c;
                }
                
                .form-group {
                    margin-bottom: 1.25rem;
                }
                
                .form-label {
                    display: block;
                    color: #374151;
                    margin-bottom: 0.5rem;
                    font-weight: 500;
                }
                
                .form-input {
                    width: 100%;
                    border: 1px solid #d1d5db;
                    padding: 0.75rem;
                    border-radius: 0.375rem;
                    transition: border-color 0.15s ease-in-out;
                }
                
                .form-input:focus {
                    outline: none;
                    border-color: #007BFF;
                    box-shadow: 0 0 0 3px rgba(0, 169, 157, 0.1);
                }
                
                .form-select {
                    width: 100%;
                    border: 1px solid #d1d5db;
                    padding: 0.75rem;
                    border-radius: 0.375rem;
                    background-color: white;
                    transition: border-color 0.15s ease-in-out;
                }
                
                .form-select:focus {
                    outline: none;
                    border-color: #007BFF;
                    box-shadow: 0 0 0 3px rgba(0, 169, 157, 0.1);
                }
                
                .submit-button {
                    width: 100%;
                    background-color: #f33a3a;
                    color: #fff;
                    padding: 0.75rem;
                    border-radius: 0.375rem;
                    cursor: pointer;
                    font-weight: bold;
                    border: none;
                    transition: background-color 0.2s;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                }
                
                .submit-button:hover {
                    background-color: #e02e2e;
                }
                
                .submit-button:disabled {
                    opacity: 0.7;
                    cursor: not-allowed;
                }
                
                .signup-link {
                    text-align: center;
                    margin-top: 1.5rem;
                    color: #4b5563;
                }
                
                .signup-link a {
                    color: #007BFF;
                    text-decoration: none;
                    font-weight: 500;
                }
                
                .signup-link a:hover {
                    text-decoration: underline;
                }
                
                .spinner {
                    border: 3px solid rgba(255, 255, 255, 0.3);
                    border-radius: 50%;
                    border-top: 3px solid white;
                    width: 1rem;
                    height: 1rem;
                    animation: spin 1s linear infinite;
                    margin-right: 0.5rem;
                }
                
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
            `}</style>

      <div className="login-container">
        {/* Logo */}
        <div className="logo-container">
          <div className="logo-icon">S</div>
          <span className="logo-text">
            Shopify <span className="logo-text-bold">Scraper</span>
          </span>
        </div>

        <h2 className="form-title">Sign In to Your Account</h2>

        {message && <div className={`message ${isSuccess ? "message-success" : "message-error"}`}>{message}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Role</label>
            <select name="role" value={formData.role} onChange={handleChange} className="form-select">
              <option value="admin">Admin</option>
              <option value="client">Client</option>
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Email Address</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="form-input"
              placeholder="Enter your email"
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Password</label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              className="form-input"
              placeholder="Enter your password"
              required
            />
          </div>

          <div className="form-group" style={{ display: "flex", alignItems: "center", marginBottom: "1.5rem" }}>
            <input
              type="checkbox"
              name="rememberMe"
              id="rememberMe"
              checked={formData.rememberMe}
              onChange={handleChange}
              style={{ marginRight: "0.5rem" }}
            />
            <label htmlFor="rememberMe" style={{ margin: 0, cursor: "pointer" }}>
              Remember me for 7 days
            </label>
          </div>

          <button type="submit" className="submit-button" disabled={isLoading}>
            {isLoading ? (
              <>
                <div className="spinner"></div>
                Signing In...
              </>
            ) : (
              "Sign In"
            )}
          </button>
        </form>

        <p className="signup-link">
          Don't have an account? <Link to="/signup">Create an account</Link>
        </p>
      </div>
    </div>
  )
}

export default Login
